<script setup lang="ts">
import { useProgrammes } from '@/composables/queries/useProgrammes';
import { useTertiaryLevels } from '@/composables/queries/useTertiaryLevels';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import { useProgrammeModules } from '@/composables/queries/useProgrammeModules';
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';
import { ProgrammeDeliveryModeEnum } from '@/types/composition-functions';
import { useToast } from 'primevue/usetoast';

interface CourseItem {
  id: string;
  course_code: string;
  course_title: string;
  unit: number;
}

const props = defineProps<{
  courses: CourseItem[];
}>();

const emit = defineEmits<{ success: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const toast = useToast();
const { programmes } = useProgrammes({ autoFetch: true, first: 200, page: 1 });
const { levelOptions } = useTertiaryLevels();
const { academicCalendars } = useSchoolQuery();
const { createTertiaryCurriculum, creatingCurriculum } = useTertiaryMutations();

const programmeId = ref('');
const level = ref('');
const semester = ref('');
const status = ref<'C' | 'E'>('C');
const programmeModuleId = ref<string | null>(null);

const programmeOptions = computed(() =>
  programmes.value.map((p: { id: string; prog_code: string; program_name: string }) => ({
    label: `${p.prog_code} - ${p.program_name}`,
    value: p.id,
  })),
);

const selectedProgrammeUsesModules = computed(() => {
  const programme = programmes.value.find(p => p.id === programmeId.value);
  return programme?.delivery_mode === ProgrammeDeliveryModeEnum.Modular
    || programme?.delivery_mode === ProgrammeDeliveryModeEnum.Residence;
});

const moduleVariables = computed(() => ({ programme_id: programmeId.value }));
const { modules, loadingModules } = useProgrammeModules(moduleVariables);
const moduleOptions = computed(() =>
  modules.value.map(m => ({
    label: `${m.name} (${m.started_at} – ${m.ended_at}) — ${m.is_open ? 'open' : (m.is_active ? 'out of window' : 'inactive')}`,
    value: m.id,
  })),
);

watch(programmeId, () => {
  programmeModuleId.value = null;
});

const termOptions = computed(() => {
  const active = academicCalendars.value?.find((c) => c?.is_active);
  if (!active) return [{ label: '1st Semester', value: '1' }, { label: '2nd Semester', value: '2' }];
  const structures = academicCalendars.value
    ?.map((c) => c?.termStructure)
    .filter((t, i, arr) => t && arr.findIndex((x) => x?.id === t.id) === i) ?? [];
  return structures.map((t, i) => ({ label: t?.name ?? `Semester ${i + 1}`, value: String(i + 1) }));
});

const statusOptions = [
  { label: 'Compulsory', value: 'C' },
  { label: 'Elective', value: 'E' },
];

const canSubmit = computed(() =>
  programmeId.value && level.value && semester.value && props.courses.length > 0,
);

const totalUnits = computed(() => props.courses.reduce((s, c) => s + c.unit, 0));

async function handleSubmit() {
  if (!canSubmit.value) return;
  try {
    const input = {
      programme_id: programmeId.value,
      course_registration_level: level.value,
      semester: Number(semester.value),
      programme_module_id: (selectedProgrammeUsesModules.value && programmeModuleId.value) || undefined,
      course: props.courses.map(c => ({ course_id: c.id, status: status.value })),
    };
    await createTertiaryCurriculum(input);
    toast.add({
      severity: 'success',
      summary: 'Curriculum Created',
      detail: `${props.courses.length} course(s) added to curriculum.`,
      life: 3000,
    });
    resetForm();
    visible.value = false;
    emit('success');
  } catch (e: unknown) {
    toast.add({ severity: 'error', summary: 'Error', detail: (e as Error).message, life: 5000 });
  }
}

function resetForm() {
  programmeId.value = '';
  level.value = '';
  semester.value = '';
  status.value = 'C';
  programmeModuleId.value = null;
}
</script>

<template>
  <Dialog v-model:visible="visible" header="Add Courses to Curriculum" modal :style="{ width: '520px' }" @hide="resetForm">
    <div class="space-y-4 pt-2">
      <p class="text-sm text-gray-600">
        Add <strong>{{ courses.length }}</strong> course(s) ({{ totalUnits }} units) to a curriculum.
      </p>

      <div class="bg-gray-50 rounded-lg p-2 max-h-32 overflow-y-auto">
        <div v-for="c in courses" :key="c.id" class="text-xs text-gray-700 py-0.5">
          <span class="font-medium">{{ c.course_code }}</span> — {{ c.course_title }} ({{ c.unit }}u)
        </div>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-gray-600">Programme *</label>
        <Select v-model="programmeId" :options="programmeOptions" optionLabel="label" optionValue="value"
          placeholder="Select Programme" filter class="w-full" />
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Level *</label>
          <Select v-model="level" :options="levelOptions" optionLabel="label" optionValue="classroomName"
            placeholder="Select Level" class="w-full" />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-gray-600">Semester *</label>
          <Select v-model="semester" :options="termOptions" optionLabel="label" optionValue="value"
            placeholder="Select Semester" class="w-full" />
        </div>
      </div>

      <div v-if="selectedProgrammeUsesModules" class="flex flex-col gap-1">
        <label class="text-xs font-medium text-gray-600">Module</label>
        <Select v-model="programmeModuleId" :options="moduleOptions" optionLabel="label" optionValue="value"
          placeholder="Select Module (optional)" :loading="loadingModules" showClear class="w-full" />
        <p class="text-[11px] text-gray-400">This programme is modular — assign the courses to the module they are taught in.</p>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-gray-600">Status *</label>
        <SelectButton v-model="status" :options="statusOptions" optionLabel="label" optionValue="value" :allowEmpty="false" />
      </div>
    </div>

    <template #footer>
      <Button label="Cancel" severity="secondary" text @click="visible = false" />
      <Button label="Add to Curriculum" icon="pi pi-check" :loading="creatingCurriculum"
        :disabled="!canSubmit" @click="handleSubmit" />
    </template>
  </Dialog>
</template>
